import type { DispensationEvidenceListModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { formatDispensationPeriodLabel } from "./dispensationUtils";
import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatBoolean, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface DispensationerTableProps {
  dispensations: DispensationEvidenceListModel[];
  isLoading: boolean;
  error: Error | null;
  periodLabelLookup: Map<string, string>;
  onSelectDispensation: (dispensationId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const DispensationerTable = ({
  dispensations,
  isLoading,
  error,
  periodLabelLookup,
  onSelectDispensation,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: DispensationerTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Periode</th>
        <th>Lektioner</th>
        <th>Status</th>
        <th>Uploadet den</th>
        <th>Rapporteret</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={7} className="text-muted">
              Henter dispensationer ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={7}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente dispensationer."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          dispensations.map((dispensation) => (
            <tr
              key={dispensation.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectDispensation(dispensation.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectDispensation(dispensation.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {dispensation.fileName ?? "-"}
                </div>
              </td>
              <td>
                {formatDispensationPeriodLabel(
                  dispensation.externalEfteruddanelseperiodeId,
                  periodLabelLookup,
                )}
              </td>
              <td>{dispensation.numberOfLessons ?? "-"}</td>
              <td>
                <StatusBadge
                  status={formatEvidenceStatus(dispensation.status)}
                  variant={getEvidenceStatusVariant(dispensation.status)}
                />
              </td>
              <td>{formatDate(dispensation.createdAt)}</td>
              <td>{formatBoolean(dispensation.isReported)}</td>
              <td>
                <button
                  id={`efteruddannelse-dispensation-${dispensation.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectDispensation(dispensation.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !dispensations.length && (
          <tr>
            <td colSpan={7} className="text-muted">
              Ingen dispensationer
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
